import { chakra, Flex, Heading, Icon, type FlexProps } from '@chakra-ui/react'
import { useId, type ElementType, type ReactNode } from 'react'
import {
  LuBird,
  LuHand,
  LuHourglass,
  LuIdCard,
  LuKeyRound,
  LuMail,
  LuMapPin,
  LuMessageCircle,
  LuRainbow,
  LuScanFace,
  LuShieldCheck,
} from 'react-icons/lu'
import { ROOMY, SHORT } from '~/theme/media'

const Svg = chakra('svg')

// Neutral symbols only: no people, no places, no cultural references.
const SYMBOL = {
  about: LuHand,
  email: LuMail,
  place: LuMapPin,
  face: LuScanFace,
  document: LuIdCard,
  password: LuKeyRound,
  wait: LuHourglass,
  done: LuRainbow,
  contact: LuMessageCircle,
  shield: LuShieldCheck,
  peace: LuBird,
} as const satisfies Record<string, ElementType>
export type ArtName = keyof typeof SYMBOL

// Three pastel arcs behind the symbol, drawn very faint. Same hues in both colour modes; the opacity keeps them quiet.
const ARCS = [
  ['#e8a0a0', 38],
  ['#e8d08c', 33],
  ['#8cb8e0', 28],
] as const

/**
 * A decorative, formal emblem: a soft sky disc (deco blue tint fading to the page colour, so it follows light and
 * dark mode), a thin ring, faint rainbow arcs and one Lucide line symbol. Pure inline SVG: transparent outside the
 * disc, nothing fetched. `aria-hidden` because the heading next to it already says what the step is.
 */
export function Illustration({ name, ...rest }: { name: ArtName } & Omit<FlexProps, 'children'>) {
  const gid = useId()
  return (
    <Flex
      aria-hidden
      data-art={name}
      flexShrink={0}
      align='center'
      justify='center'
      position='relative'
      // 40 px on a phone, 56 px on a tablet or a computer; left out under 500 px tall (a phone in landscape), where
      // every pixel of height goes to the page (design spec 2026-10-30 §2.2).
      w={10}
      h={10}
      css={{ ...(rest.w === undefined && { [ROOMY]: { width: '14', height: '14' } }), [SHORT]: { display: 'none' } }}
      {...rest}
    >
      <Svg viewBox='0 0 100 100' position='absolute' inset={0} w='full' h='full' focusable='false'>
        <defs>
          <linearGradient id={gid} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0' style={{ stopColor: 'var(--chakra-colors-deco-blue-bg)' }} />
            <stop offset='1' style={{ stopColor: 'var(--chakra-colors-bg-subtle)' }} />
          </linearGradient>
        </defs>
        <circle cx='50' cy='50' r='48' fill={`url(#${gid})`} />
        <circle
          cx='50'
          cy='50'
          r='47'
          fill='none'
          strokeWidth='1'
          style={{ stroke: 'var(--chakra-colors-deco-blue-solid)' }}
          opacity='0.45'
        />
        <circle
          cx='50'
          cy='50'
          r='42'
          fill='none'
          strokeWidth='0.5'
          style={{ stroke: 'var(--chakra-colors-deco-blue-solid)' }}
          opacity='0.25'
        />
        {ARCS.map(([color, r]) => (
          <path
            key={r}
            d={`M ${50 - r} 66 A ${r} ${r} 0 0 1 ${50 + r} 66`}
            fill='none'
            stroke={color}
            strokeWidth='2.5'
            strokeLinecap='round'
            opacity='0.3'
          />
        ))}
      </Svg>
      <Icon as={SYMBOL[name]} position='relative' boxSize='42%' strokeWidth={1.5} color='deco.blue.fg' />
    </Flex>
  )
}

/** A page heading with its emblem beside it (the pages that are not one of the 5 steps). */
export function ArtHeading({ title, art }: { title: ReactNode; art: ArtName }) {
  return (
    <Flex align='center' justify='space-between' gap={4}>
      <Heading
        as='h1'
        minW={0}
        textWrap='balance'
        css={{ fontSize: '2xl', lineHeight: '1.25', [ROOMY]: { fontSize: '1.75rem' } }}
      >
        {title}
      </Heading>
      <Illustration name={art} />
    </Flex>
  )
}
